// "Add a boat" from the Trips page: creates a log-only boat (ItemTracking's
// TRACKING_TRIPS) with a Captain's Log ready to write in, then hands the
// caller the created item to navigate to.
//
// A name-first modal, deliberately unlike the Items page's create-then-edit
// flow. That convention exists because creation there writes only {name} and
// the type is just another edit-modal row -- but here the button IS the type
// choice (the user tapped "Add a boat", so stamping itemType boat is their
// gesture, not a create-time picker), and two rows are created together; an
// abandoned "New Item" + Captain's Log pair would haunt this page as an empty
// section.
//
// No first trip is auto-created: whether the user's first record is an
// upcoming trip or a past log entry is their next choice, and the item page
// they land on offers both. A stub leg dated today would be a write they
// never asked for when their goal is logging last summer.
//
// The logbook create is non-fatal: the boat exists either way, and the item
// page can add a Captain's Log by hand. Failing the whole gesture over the
// second write would strand a created boat behind an error message.

import { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import * as Database from '../../utilities/Database.mjs';
import { ITEM_TYPE_BOAT } from '../../fleet-shared/ItemTypes.mjs';
import { TRACKING_TRIPS } from '../../fleet-shared/ItemTracking.mjs';
import { LOGBOOK_TYPE_CAPTAINS, defaultNameForType } from '../../fleet-shared/LogbookTypes.mjs';
import { extractErrorText } from '../../fleet-shared/Common.mjs';

const AddBoatModal = ({ show, onHide, onAdded }) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function confirm(e) {
    // The input sits in a <Form>, so Enter submits; this modal is the page.
    e?.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the boat a name.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await Database.createItem({
        name: trimmed,
        itemType: ITEM_TYPE_BOAT,
        tracking: TRACKING_TRIPS,
      });
      try {
        await Database.createLogbook({
          itemId: created.id,
          name: defaultNameForType(LOGBOOK_TYPE_CAPTAINS),
          logbookType: LOGBOOK_TYPE_CAPTAINS,
        });
      } catch (err) {
        // Non-fatal (see header). The navigation still happens; the item
        // page's empty logbook state is the recovery path. The warn is the
        // only signal this case leaves anywhere, so keep it.
        console.warn('AddBoatModal: boat created but its Captain\'s Log was not', err);
      }
      // Before onAdded: the caller navigates away and unmounts this modal, but
      // that is the CALLER's behaviour -- releasing busy here keeps the modal
      // usable for a future caller that doesn't navigate.
      setBusy(false);
      onAdded(created);
    } catch (err) {
      setError(extractErrorText(err));
      setBusy(false);
    }
  }

  // Reset on dismiss: reopening is a fresh "add a boat", not a resumed one.
  function handleHide() {
    setName('');
    setError(null);
    onHide();
  }

  return (
    <Modal show={show} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>Add a boat</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={confirm}>
          <Form.Group>
            <Form.Label>Boat name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Boat name"
              autoFocus
            />
          </Form.Group>
        </Form>
        <p className="text-body-secondary small mb-0 mt-3">
          Sets up a boat for trips and a Captain's Log — no maintenance
          tracking. You can turn on full tracking later from the boat's page.
        </p>
        {error && (
          <p className="text-danger small mb-0 mt-3" role="alert">{error}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={busy}>Cancel</Button>
        <Button variant="success" onClick={confirm} disabled={busy}>Add boat</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddBoatModal;
